(() => {
  'use strict';
  const configuration = document.getElementById('bead-bucks-config');
  if (!configuration || window.BeadBucks) return;
  let config;
  try { config = JSON.parse(configuration.textContent); } catch { return; }
  const number = value => Number.isSafeInteger(value) && value >= 0;
  const rewardCode = code => /^(?:BB2O-[0-9a-f]{32}|BEADBUCKS-[2-9a-hj-np-z]{8})$/i.test(String(code || ''));
  const rewardTitle = title => rewardCode(title) || title === 'Bead Bucks -Web';
  const text = (key, values = {}) => (config.strings[key] || '').replace(/\{(\w+)\}/g, (match, name) => String(values[name] ?? match));
  const count = value => new Intl.NumberFormat(document.documentElement.lang || 'en-CA').format(value);
  const currency = cents => new Intl.NumberFormat(document.documentElement.lang || 'en-CA', { style: 'currency', currency: model.summary?.currency || config.currency, currencyDisplay: 'narrowSymbol' }).format(cents / 100);
  const failure = key => Object.assign(new Error(text(key)), { beadBucks: true });
  const panels = new Set();
  const model = { summary: null, cart: null, selection: null, attempt: null, issued: null, cancelling: null, preparation: null, checkout: null, error: '', history: null, historyError: false, historyPage: 1, updated: 0 };
  const requests = { summary: null, cart: null, cancel: null, history: null };
  let cartRevision = 0;
  let summaryError = null;
  let availabilityTimer = null;
  let availabilityAttempt = 0;
  const storageKey = ['bead-bucks', 'v3', config.shop, config.theme, config.customerId || 'guest'].join(':');
  const validSelection = value => value && Number.isSafeInteger(value.dollars) && value.dollars > 0 && Number.isSafeInteger(value.rate) && value.rate > 0 && /^[A-Z]{3}$/.test(value.currency) && typeof value.requestKey === 'string' && value.requestKey.length >= 20;
  try {
    const stored = JSON.parse(sessionStorage.getItem(storageKey));
    if (stored && number(stored.at) && Date.now() - stored.at < 14400000) {
      model.selection = validSelection(stored.selection) ? stored.selection : null;
      model.attempt = validSelection(stored.attempt) ? stored.attempt : null;
      model.issued = typeof stored.issued === 'string' ? stored.issued : null;
      model.cancelling = typeof stored.cancelling === 'string' ? stored.cancelling : null;
    }
  } catch {}
  const save = () => {
    try { sessionStorage.setItem(storageKey, JSON.stringify({ at: Date.now(), selection: model.selection, attempt: model.attempt, issued: model.issued, cancelling: model.cancelling })); } catch {}
  };
  const activeReward = () => model.summary?.reward?.channel === 'online' ? model.summary.reward : null;
  const heldPoints = () => {
    const reward = activeReward();
    return reward && !['RELEASED', 'SETTLED', 'ORDER_LINKED'].includes(reward.phase) ? reward.points : 0;
  };
  const shownBalance = () => Math.max(0, (model.summary?.balance || 0) - Math.max(0, (model.selection?.dollars || 0) * (model.selection?.rate || 0) - heldPoints()));
  const showError = error => { model.error = error?.message || text('unavailable'); repaint(); };

  async function request(url, body) {
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), 20000);
    try {
      const response = await fetch(url, { method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) }, ...(body ? { body: JSON.stringify(body) } : {}), signal: abort.signal });
      const result = await response.json();
      if (!response.ok || result?.ok === false) {
        const message = { NOT_ELIGIBLE: 'ineligible', REDEMPTION_PAUSED: 'paused', RATE_CHANGED: 'rate_changed', BALANCE_CHANGED: 'smaller', REWARD_PENDING: 'processing' }[result?.code] || 'unavailable';
        const error = failure(message);
        error.code = result?.code;
        throw error;
      }
      return result;
    } catch (error) {
      if (error.beadBucks) throw error;
      throw failure('unavailable');
    } finally { clearTimeout(timer); }
  }

  function validateReward(reward) {
    if (!reward || typeof reward.id !== 'string' || !['online', 'pos'].includes(reward.channel) || !number(reward.points) || !number(reward.valueCents) || !Number.isFinite(Date.parse(reward.expiresAt))) throw failure('unavailable');
    return reward;
  }

  function validateSummary(summary) {
    if (summary?.customerId !== `gid://shopify/Customer/${config.customerId}` || summary.checkoutPreparation !== 2 || !number(summary.balance) || !/^[A-Z]{3}$/.test(summary.currency) || typeof summary.csrf !== 'string' || typeof summary.eligible !== 'boolean' || typeof summary.redemption?.enabled !== 'boolean') throw failure('unavailable');
    if (summary.redemption.enabled && (!number(summary.redemption.rate) || summary.redemption.rate < 1)) throw failure('unavailable');
    if (summary.reward) validateReward(summary.reward);
    return summary;
  }

  function readSummary(force = false) {
    if (!config.customerId) return Promise.resolve(null);
    if (requests.summary) return requests.summary;
    if (!force && model.summary && Date.now() - model.updated < 30000) return Promise.resolve(model.summary);
    requests.summary = request(config.endpoint + '?view=summary').then(validateSummary).then(summary => {
      const previous = model.issued;
      model.summary = summary;
      if (summaryError && model.error === summaryError.message) model.error = '';
      summaryError = null;
      model.updated = Date.now();
      const reward = activeReward();
      if (previous && !reward && !model.cancelling && !model.attempt) model.selection = null;
      if (reward) {
        model.issued = reward.id;
        if (!model.selection && !model.cancelling && !model.attempt && reward.usable) model.selection = { dollars: reward.valueCents / 100, rate: reward.rate, currency: reward.currency, requestKey: crypto.randomUUID() };
      } else model.issued = null;
      save();
      return summary;
    }).catch(error => { summaryError = error; throw error; }).finally(() => { requests.summary = null; repaint(); });
    return requests.summary;
  }

  function scheduleAvailabilityCheck() {
    const summary = model.summary;
    const interrupted = Boolean(summaryError || (summary?.eligible && summary.redemption.configured && !summary.redemption.enabled));
    if (!config.customerId || !panels.size || document.hidden || !interrupted) {
      clearTimeout(availabilityTimer);
      availabilityTimer = null;
      if (!interrupted) availabilityAttempt = 0;
      return;
    }
    if (availabilityTimer !== null || requests.summary || model.checkout || model.preparation || requests.cancel) return;
    const delays = [2000, 5000, 10000, 30000];
    availabilityTimer = setTimeout(() => {
      availabilityTimer = null;
      if (!panels.size || document.hidden || model.checkout || model.preparation || requests.cancel || requests.summary) return;
      availabilityAttempt++;
      readSummary(true).catch(() => {});
    }, delays[Math.min(availabilityAttempt, delays.length - 1)]);
  }

  function acceptCart(cart) {
    if (!Array.isArray(cart?.items) || !number(cart.total_price) || !/^[A-Z]{3}$/.test(cart.currency)) throw failure('unavailable');
    model.cart = cart;
    cartRevision++;
    return cart;
  }

  async function readCart(force = false) {
    if (force && requests.cart) await requests.cart.catch(() => {});
    if (!requests.cart) {
      const revision = cartRevision;
      requests.cart = request(config.root + 'cart.js').then(cart => revision === cartRevision ? acceptCart(cart) : model.cart).finally(() => { requests.cart = null; });
    }
    return requests.cart;
  }

  function discounts(cart) {
    const order = (cart?.cart_level_discount_applications || []).map(entry => {
      const application = entry.discount_application || entry;
      return { application, amount: entry.amount ?? application.total_allocated_amount };
    });
    const lines = (cart?.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(allocation => ({ application: allocation.discount_application, amount: allocation.amount })));
    return order.concat(lines);
  }
  const isReward = application => application?.type === 'discount_code' && rewardTitle(application.title);
  const appliedValue = cart => discounts(cart).reduce((sum, entry) => sum + (isReward(entry.application) ? Number(entry.amount) || 0 : 0), 0);
  function eligibleSpend(cart = model.cart) {
    if (!cart) return 0;
    const excluded = cart.items.reduce((sum, item) => sum + (item.gift_card ? Number(item.final_line_price) || 0 : 0), 0);
    return Math.max(0, cart.total_price + appliedValue(cart) - excluded);
  }
  function partialMinimumAllowed() {
    const reward = activeReward();
    return model.summary?.redemption.minimumRewardPartialUse === true && (!reward || reward.forfeitRemainder === true);
  }
  function rewardApplied(cart, reward) {
    const applied = appliedValue(cart);
    const expected = reward.forfeitRemainder === true && reward.valueCents === 100
      ? Math.min(reward.valueCents, eligibleSpend(cart))
      : reward.valueCents;
    return expected > 0 && applied === expected && cart.discount_codes?.some(entry => entry.applicable && entry.code?.toUpperCase() === reward.code.toUpperCase());
  }
  function maximum() {
    const summary = model.summary;
    if (!summary?.eligible || !summary.redemption.enabled || model.cart?.currency !== summary.currency || summary.reward?.channel === 'pos') return 0;
    const spend = eligibleSpend();
    const limit = spend > 0 && spend < 100 && partialMinimumAllowed() ? 1 : Math.floor(spend / 100);
    return Math.min(Math.floor((summary.balance + heldPoints()) / summary.redemption.rate), limit);
  }
  const comparable = (reward, choice) => reward && choice && reward.valueCents === choice.dollars * 100 && reward.rate === choice.rate && reward.currency === choice.currency;

  async function sendReward(body) {
    await readSummary();
    try {
      const result = await request(config.endpoint, { ...body, csrf: model.summary.csrf });
      if (typeof result.csrf === 'string') model.summary.csrf = result.csrf;
      return result;
    } catch (error) {
      if (error.code !== 'REFRESH_REQUIRED') throw error;
      await readSummary(true);
      const result = await request(config.endpoint, { ...body, csrf: model.summary.csrf });
      if (typeof result.csrf === 'string') model.summary.csrf = result.csrf;
      return result;
    }
  }

  async function resolveAttempt() {
    if (!model.attempt) return;
    const attempted = { ...model.attempt };
    await readSummary(true);
    let reward = activeReward();
    if (!reward) {
      const result = await issue(attempted);
      reward = validateReward(result.reward);
      model.summary.reward = reward;
      await readSummary(true);
    }
    model.issued = reward.id;
    model.attempt = null;
    if (!comparable(reward, model.selection) || reward.cancelling) model.cancelling = reward.id;
    save();
  }

  async function issue(selection) {
    try { return await sendReward({ intent: 'redeem', ...selection }); }
    catch (error) {
      if (['NOT_ELIGIBLE', 'REDEMPTION_PAUSED', 'CURRENCY_MISMATCH', 'RATE_CHANGED', 'BALANCE_CHANGED', 'INVALID_REQUEST'].includes(error.code)) {
        model.attempt = null;
        if (model.selection?.requestKey === selection.requestKey) model.selection = null;
        save();
      }
      throw error;
    }
  }

  const currentCartSnapshot = snapshot => snapshot && Number.isInteger(snapshot.revision) && snapshot.revision === window.Shopify?.cartMutationRevision && (!model.cart || model.cart === snapshot.cart);

  async function updateCodes(reward = null, coupon = '', options = {}) {
    if (!window.Shopify?.queueCartMutation) throw failure('unavailable');
    const reusable = currentCartSnapshot(options.snapshot);
    return window.Shopify.queueCartMutation(async () => {
      if (reusable && window.Shopify.cartMutationRevision === options.snapshot.revision + 1 && model.cart === options.snapshot.cart) acceptCart(options.snapshot.cart);
      else await readCart(true);
      const before = model.cart;
      if (!Array.isArray(before.discount_codes)) throw failure('unavailable');
      const values = before.discount_codes.map(entry => entry.code).concat(String(coupon).split(','));
      const retained = values.filter(value => typeof value === 'string' && value.trim() && !rewardCode(value.trim())).map(value => value.trim());
      if (reward) retained.push(reward.code);
      const unique = Array.from(new Map(retained.map(value => [value.toUpperCase(), value])).values());
      await request(config.root + 'cart/update.js', { discount: unique.join(',') });
      const after = await readCart(true);
      window.Shopify.notifyCart(after);
      repaint();
      if (!Array.isArray(after.discount_codes)) throw failure('unavailable');
      if (!reward) {
        if (after.discount_codes.some(entry => rewardCode(entry.code))) throw failure('unavailable');
      } else {
        if (!rewardApplied(after, reward)) throw failure('not_applied');
        const other = discounts(before).filter(entry => entry.amount > 0 && !isReward(entry.application));
        const resulting = discounts(after).filter(entry => entry.amount > 0 && !isReward(entry.application));
        const discountType = application => application.type || 'automatic';
        if (other.some(entry => !resulting.some(next => next.application.title === entry.application.title && discountType(next.application) === discountType(entry.application)))) throw failure('combination');
      }
      if (options.render !== false && window.refreshOnlineBufferCart) await window.refreshOnlineBufferCart(after);
      return { cart: after, revision: window.Shopify.cartMutationRevision };
    });
  }

  function cancelReward() {
    if (requests.cancel) return requests.cancel;
    requests.cancel = (async () => {
      await resolveAttempt();
      const id = model.cancelling;
      if (id) {
        await sendReward({ intent: 'cancel', rewardId: id });
        await updateCodes();
        await readSummary(true);
        if (activeReward()?.id === id && activeReward().phase !== 'RELEASED') throw failure('returning');
        model.cancelling = null;
        model.issued = null;
        save();
      } else if (model.cart?.discount_codes?.some(entry => rewardCode(entry.code))) await updateCodes();
    })().finally(() => { requests.cancel = null; repaint(); });
    repaint();
    return requests.cancel;
  }

  function select(dollars) {
    if (model.checkout) return;
    if (!Number.isSafeInteger(dollars) || dollars < 1 || dollars > maximum()) { showError(failure('smaller')); return; }
    const previous = model.selection;
    model.selection = { dollars, rate: model.summary.redemption.rate, currency: model.summary.currency, requestKey: previous?.dollars === dollars && previous.rate === model.summary.redemption.rate ? previous.requestKey : crypto.randomUUID() };
    const reward = activeReward();
    if (reward && (!comparable(reward, model.selection) || reward.cancelling)) model.cancelling = reward.id;
    model.error = '';
    save();
    repaint();
    prepareSelection().catch(showError);
  }

  async function remove() {
    if (model.checkout) return;
    model.selection = null;
    model.error = '';
    if (activeReward()) model.cancelling = activeReward().id;
    save();
    repaint();
    if (model.preparation) await model.preparation.catch(() => {});
    if (model.cancelling || model.attempt || model.cart?.discount_codes?.some(entry => rewardCode(entry.code))) await cancelReward();
    if (!model.selection && !activeReward() && !model.attempt && !model.cancelling) {
      model.error = '';
      repaint();
    }
  }

  function checkSelection(desired) {
    if (!model.selection || model.selection.requestKey !== desired.requestKey) throw failure('changed');
    if (!model.summary.eligible || !model.summary.redemption.enabled) throw failure('paused');
    if (model.summary.currency !== desired.currency || model.cart.currency !== desired.currency) throw failure('currency');
    if (model.summary.redemption.rate !== desired.rate) {
      model.selection = null;
      save();
      throw failure('rate_changed');
    }
    if (desired.dollars > maximum()) throw failure('smaller');
    const reward = activeReward();
    if (reward && (!comparable(reward, desired) || reward.cancelling || !['REQUESTED', 'HELD', 'ISSUING', 'ACTIVE'].includes(reward.phase) || Date.parse(reward.expiresAt) <= Date.now())) throw failure('processing');
    return reward;
  }

  async function prepareSelectedReward() {
    await window.Shopify.whenCartChangesComplete();
    while (true) {
      await resolveAttempt();
      const current = activeReward();
      if (current && (!comparable(current, model.selection) || current.cancelling)) model.cancelling = current.id;
      if (model.cancelling || requests.cancel) await cancelReward();
      if (!model.selection) return;
      const desired = { ...model.selection };
      await Promise.all([readSummary(true), readCart(true)]);
      if (model.selection?.requestKey !== desired.requestKey) continue;
      let reward = checkSelection(desired);
      if (reward?.usable) return;
      model.attempt = desired;
      save();
      repaint();
      const result = await issue(desired);
      reward = validateReward(result.reward);
      model.summary.reward = reward;
      model.issued = reward.id;
      model.attempt = null;
      if (!reward.usable || !rewardCode(reward.code) || !comparable(reward, desired)) {
        model.cancelling = reward.id;
        save();
        await cancelReward();
        throw failure('not_applied');
      }
      if (model.selection?.requestKey !== desired.requestKey) model.cancelling = reward.id;
      save();
      await readSummary(true);
      if (model.selection?.requestKey === desired.requestKey && !model.cancelling) return;
    }
  }

  function prepareSelection() {
    if (model.preparation) return model.preparation;
    model.preparation = prepareSelectedReward().finally(() => { model.preparation = null; repaint(); });
    return model.preparation;
  }

  async function checkout(coupon, snapshot) {
    await window.Shopify.whenCartChangesComplete();
    if (!config.customerId) return;
    await resolveAttempt();
    if (model.cancelling || requests.cancel) await cancelReward();
    if (!model.selection) return;
    const desired = { ...model.selection };
    const revision = window.Shopify.cartMutationRevision;
    const checks = await Promise.all([
      readSummary(true),
      currentCartSnapshot(snapshot) ? Promise.resolve(acceptCart(snapshot.cart)) : readCart(true)
    ]);
    snapshot = { cart: checks[1], revision };
    let reward = checkSelection(desired);
    let issued = false;
    if (!reward || !reward.usable) {
      model.attempt = desired;
      save();
      const result = await issue(desired);
      reward = validateReward(result.reward);
      model.summary.reward = reward;
      model.issued = reward.id;
      model.attempt = null;
      save();
      issued = true;
    }
    try {
      if (!reward.usable || !rewardCode(reward.code) || !comparable(reward, desired)) throw failure('not_applied');
      const results = await Promise.allSettled([
        updateCodes(reward, coupon, { snapshot, render: false }),
        issued ? readSummary(true) : Promise.resolve()
      ]);
      const rejected = results.find(result => result.status === 'rejected');
      if (rejected) throw rejected.reason;
      const applied = results[0].value;
      await window.Shopify.whenCartChangesComplete();
      if (!currentCartSnapshot(applied) || model.cart !== applied.cart) await readCart(true);
      if (!rewardApplied(model.cart, reward)) throw failure('not_applied');
    } catch (error) {
      model.selection = null;
      model.cancelling = reward.id;
      save();
      try { await cancelReward(); } catch { model.error = text('returning'); }
      throw error;
    }
  }

  function prepareCheckout(coupon = '', snapshot = null) {
    if (model.checkout) return model.checkout;
    model.error = '';
    model.checkout = (async () => {
      if (model.preparation) await model.preparation;
      return checkout(coupon, snapshot);
    })().catch(error => { showError(error); throw error; }).finally(() => { model.checkout = null; repaint(); });
    repaint();
    return model.checkout;
  }

  function project({ root, total, discounts: amounts, data, intents = [] }) {
    if (root.id !== 'main-cart-items') return { total, estimated: false };
    const existing = Array.from(amounts).reduce((sum, [title, amount]) => sum + (rewardTitle(title) ? amount : 0), 0);
    const managing = Boolean(model.selection || model.cancelling || model.attempt);
    const excluded = (model.cart?.items || []).filter(item => item.gift_card).reduce((sum, item) => {
      const price = data?.items.find(row => row.key === item.key);
      const intent = intents.find(change => change.key === item.key || change.keys.includes(item.key));
      const quantity = intent ? intent.quantity : price?.quantity ?? item.quantity;
      return sum + quantity * (price?.final ?? item.final_price);
    }, 0);
    const spend = Math.max(0, total + existing - excluded);
    const desired = model.selection ? Math.min(model.selection.dollars * 100, spend) : 0;
    const pending = managing && (desired !== existing || Boolean(model.cancelling));
    if (pending) for (const title of amounts.keys()) if (rewardTitle(title)) amounts.set(title, 0);
    const result = managing ? Math.max(0, total + existing - desired) : total;
    root.querySelectorAll('[data-bb-total]').forEach(row => {
      row.hidden = !pending || !desired;
      const value = row.querySelector('[data-bb-total-amount]');
      if (value) value.textContent = '−' + currency(desired);
    });
    root.querySelectorAll('bead-bucks-panel').forEach(panel => panel.updateEarning?.(result));
    return { total: result, estimated: pending };
  }

  function withHistorySamples(history) {
    const theme = window.Shopify?.theme;
    if (config.shop !== 'i-bead.myshopify.com' || config.customerId !== '5622936272' || config.theme !== '189067755669' || String(theme?.id) !== config.theme || theme?.role !== 'unpublished') return history;
    const oldest = Math.min(Date.now(), ...history.rows.map(row => Date.parse(row.at)));
    const kinds = ['earned', 'redeemed', 'returned', 'reversed'];
    const points = [60, -200, 200, -60];
    const samples = Array.from({ length: 20 }, (_, index) => ({
      id: 'bb-history-test-' + (index + 1),
      at: new Date(oldest - (index + 1) * 86400000).toISOString(),
      kind: kinds[index % 4],
      channel: index % 8 < 4 ? 'online' : 'pos',
      reference: 'TEST-' + String(index + 1).padStart(2, '0'),
      points: points[index % 4]
    }));
    return { ...history, rows: [...history.rows, ...samples], testEntries: samples.length };
  }

  function loadHistory(force = false) {
    if (!config.customerId) return Promise.resolve();
    if (requests.history) return requests.history;
    if (model.history && !force) return Promise.resolve(model.history);
    model.historyError = false;
    requests.history = request(config.endpoint + '?view=history').then(history => {
      if (history?.customerId !== `gid://shopify/Customer/${config.customerId}` || !Array.isArray(history.rows) || history.rows.some(row => !Number.isSafeInteger(row.points) || !Number.isFinite(Date.parse(row.at)))) throw failure('history_error');
      model.history = withHistorySamples(history);
      return model.history;
    }).catch(() => { model.historyError = true; }).finally(() => { requests.history = null; repaint(); });
    return requests.history;
  }

  async function refresh(force = false) {
    if (!config.customerId) return;
    model.error = '';
    try {
      if (model.preparation) await model.preparation;
      await Promise.all([readSummary(force), readCart()]);
      if (model.cancelling) await cancelReward();
      repaint();
    } catch (error) { showError(error); throw error; }
  }

  function repaint() {
    scheduleAvailabilityCheck();
    for (const panel of panels) panel.update();
    window.Shopify?.projectCartPrices?.();
    const block = Boolean(model.selection || model.attempt || model.cancelling || model.checkout);
    document.querySelectorAll('[data-cart-express]').forEach(element => {
      if (block && !element.hasAttribute('data-bb-express-hidden')) {
        element.dataset.bbExpressHidden = String(element.hidden);
        element.hidden = true;
      } else if (!block && element.hasAttribute('data-bb-express-hidden')) {
        element.hidden = element.dataset.bbExpressHidden === 'true';
        delete element.dataset.bbExpressHidden;
      }
    });
  }

  class BeadBucksPanel extends HTMLElement {
    connectedCallback() {
      panels.add(this);
      if (!this.listening) {
        this.listening = true;
        this.addEventListener('click', event => {
          const button = event.target.closest('button');
          if (!button || !this.contains(button) || button.disabled) return;
          if (button.hasAttribute('data-bb-apply')) select(Number(this.find('amount').value));
          if (button.hasAttribute('data-bb-remove')) remove().catch(showError);
          if (button.hasAttribute('data-bb-refresh')) refresh(true).catch(() => {});
          if (button.hasAttribute('data-bb-history-retry')) loadHistory(true);
          if (button.matches('[data-bb-history-page], [data-bb-history-step]')) {
            const page = button.hasAttribute('data-bb-history-page') ? Number(button.dataset.bbHistoryPage) : model.historyPage + Number(button.dataset.bbHistoryStep);
            if (Number.isSafeInteger(page)) {
              model.historyPage = page;
              this.updateHistory();
              if (!button.isConnected || button.hidden) this.find('history-pages').querySelector('[aria-current="page"]')?.focus({ preventScroll: true });
            }
          }
        });
        this.addEventListener('change', event => {
          if (event.target.matches('[data-bb-amount]')) this.find('apply').disabled = event.target.disabled || !event.target.value;
        });
        this.addEventListener('keydown', event => {
          if (event.key === 'Enter' && event.target.matches('[data-bb-amount]')) { event.preventDefault(); this.find('apply').click(); }
        });
      }
      this.update();
      if (config.customerId) {
        refresh().catch(() => {});
        if (this.dataset.view === 'account') loadHistory();
      }
    }
    disconnectedCallback() { panels.delete(this); scheduleAvailabilityCheck(); }
    find(name) { return this.querySelector(`[data-bb-${name}]`); }
    content(name, value) { const element = this.find(name); if (element && element.textContent !== value) element.textContent = value; }
    visible(name, visible) { const element = this.find(name); if (element) element.hidden = !visible; }
    update() {
      const summary = model.summary;
      const signedIn = Boolean(config.customerId);
      this.visible('login', !signedIn);
      this.visible('loading', signedIn && !summary && !model.error);
      this.visible('body', signedIn && Boolean(summary));
      this.setAttribute('aria-busy', String(signedIn && !summary && !model.error));
      if (!signedIn) return;
      const status = model.error || (model.checkout ? text('preparing') : model.cancelling ? text('returning') : model.attempt ? text('processing') : '');
      this.content('status', status);
      this.visible('status', Boolean(status));
      this.find('status')?.classList.toggle('bb-error', Boolean(model.error));
      this.visible('refresh', Boolean(model.error) && !model.checkout);
      if (!summary) return;
      const points = shownBalance();
      const rate = summary.redemption.rate;
      this.content('balance', text('balance', { points: count(points) }));
      this.content('points', count(points));
      this.content('value', rate > 0 ? currency(Math.floor(points / rate) * 100) : '—');
      this.content('rate', rate > 0 ? text('redeem_rate', { points: count(rate) }) : text('paused'));
      this.content('earn-rate', summary.earning?.enabled ? text('earn_rate', { points: count(summary.earning.rate) }) : text('paused_earning'));
      let availability = !summary.eligible ? text('ineligible') : summary.reward?.channel === 'pos' ? text('pos_pending') : !summary.redemption.enabled ? text(summary.redemption.configured ? 'temporarily_unavailable' : 'paused') : model.cart && model.cart.currency !== summary.currency ? text('currency', { currency: summary.currency }) : '';
      if (this.dataset.view === 'account') {
        this.content('value-label', availability || text('value_label'));
        if (model.cart) {
          const hasItems = model.cart.items.some(item => item.quantity > 0);
          const action = this.find('account-action');
          action.setAttribute('href', hasItems ? action.dataset.cartUrl : action.dataset.shopUrl);
          this.content('account-action-text', text(hasItems ? maximum() > 0 ? 'use_cart' : 'view_cart' : 'start_shopping'));
        }
        const selectedPoints = model.selection ? model.selection.dollars * model.selection.rate : 0;
        const reserved = heldPoints();
        this.visible('held', Boolean(reserved || selectedPoints));
        this.content('held', text(reserved ? 'reserved_account' : 'selected_points', { points: count(reserved || selectedPoints) }));
        this.visible('history-card', true);
        this.visible('how', true);
        this.updateHistory();
        return;
      }
      const max = maximum();
      if (!availability && !max) availability = text(points < rate ? 'none_available' : 'empty_cart', { points: count(rate) });
      this.content('availability', availability);
      this.visible('availability', Boolean(availability));
      this.visible('picker', !availability && max > 0);
      const spend = eligibleSpend();
      const selectBox = this.find('amount');
      const signature = [max, rate, summary.currency, model.selection?.dollars || 0].join(':');
      if (selectBox.dataset.options !== signature) {
        const previous = Number(selectBox.value);
        const amounts = Array.from({ length: Math.min(20, max) }, (_, index) => index + 1);
        for (let value = 25; value <= Math.min(200, max); value += 5) amounts.push(value);
        if (max > 0 && !amounts.includes(max)) amounts.push(max);
        if (model.selection && model.selection.dollars <= max && !amounts.includes(model.selection.dollars)) amounts.push(model.selection.dollars);
        amounts.sort((a, b) => a - b);
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = text('choose');
        placeholder.disabled = true;
        selectBox.replaceChildren(placeholder, ...amounts.map(dollars => {
          const option = document.createElement('option');
          option.value = String(dollars);
          option.textContent = text('choice', { amount: currency(dollars * 100), points: count(dollars * rate) });
          return option;
        }));
        selectBox.dataset.options = signature;
        selectBox.value = amounts.includes(previous) ? String(previous) : '';
      }
      if (this.lastSelection !== model.selection?.requestKey) {
        this.lastSelection = model.selection?.requestKey;
        if (model.selection && Array.from(selectBox.options).some(option => Number(option.value) === model.selection.dollars)) selectBox.value = String(model.selection.dollars);
        else selectBox.value = '';
      }
      selectBox.disabled = Boolean(model.checkout || !max || availability);
      this.find('apply').disabled = selectBox.disabled || !selectBox.value;
      this.content('apply', text(model.selection ? 'change' : 'apply'));
      const showSelection = Boolean(model.selection || activeReward() || model.cancelling || model.attempt);
      this.visible('selection', showSelection);
      const amount = Math.min((model.selection?.dollars || 0) * 100, spend);
      this.content('selected', model.cancelling ? text('returning') : model.selection ? text('redeeming', { points: count(heldPoints() || model.selection.dollars * model.selection.rate) }) : text('processing'));
      this.content('selected-points', model.selection && !model.cancelling ? text('redemption_amount', { amount: currency(amount) }) : '');
      this.find('remove').disabled = Boolean(model.checkout || requests.cancel);
      this.content('remove', text(model.cancelling ? 'retry_cancel' : 'remove'));
      this.updateEarning(model.cart?.total_price || 0);
    }
    updateEarning(total) {
      if (this.dataset.view === 'account') return;
      const summary = model.summary;
      const visible = Boolean(summary?.eligible && summary.earning?.enabled && model.cart?.currency === summary.currency);
      this.visible('earning', visible);
      if (visible) this.content('estimate', text('estimate', { points: count(Math.floor(Math.max(0, total) * summary.earning.rate / 100)) }));
    }
    updateHistory() {
      if (this.dataset.view !== 'account') return;
      const history = model.history;
      const rows = history?.rows || [];
      const pageSize = 5;
      const pages = Math.max(1, Math.ceil(rows.length / pageSize));
      model.historyPage = Math.min(pages, Math.max(1, model.historyPage));
      const start = (model.historyPage - 1) * pageSize;
      const end = Math.min(start + pageSize, rows.length);
      this.content('history-status', text(model.historyError ? 'history_error' : !history ? 'loading' : 'history_empty'));
      this.visible('history-status', model.historyError || !history || !rows.length);
      this.visible('history-retry', model.historyError);
      this.visible('table', !model.historyError && rows.length > 0);
      this.visible('history-pagination', pages > 1 && !model.historyError);
      this.visible('history-prev', model.historyPage > 1);
      this.visible('history-next', model.historyPage < pages);
      this.content('history-range', text('history_range', { start: count(start + 1), end: count(end), total: count(rows.length) }));
      this.find('history-progress').style.width = rows.length ? `${end / rows.length * 100}%` : '0%';
      this.visible('history-note', Boolean(rows.length));
      this.content('history-note', text('history_note') + (history?.limited ? ' ' + text('history_limited') : '') + (history?.testEntries ? ' Test preview: 20 sample entries added; your points balance is unchanged.' : ''));
      if (this.historyRendered === history && this.historyPage === model.historyPage) return;
      this.historyRendered = history;
      this.historyPage = model.historyPage;
      const element = (tag, className, value) => { const node = document.createElement(tag); if (className) node.className = className; if (value !== undefined) node.textContent = value; return node; };
      const pageButtons = [];
      let previous = 0;
      for (let page = 1; page <= pages; page++) {
        if (pages > 7 && page !== 1 && page !== pages && Math.abs(page - model.historyPage) > 1) continue;
        if (previous && page > previous + 1) {
          const gap = element('span', 'bb-page-gap', '…');
          gap.setAttribute('aria-hidden', 'true');
          pageButtons.push(gap);
        }
        const button = element('button', 'bb-page-button', count(page));
        button.type = 'button';
        button.dataset.bbHistoryPage = String(page);
        button.setAttribute('aria-label', text('history_page', { page: count(page) }));
        if (page === model.historyPage) button.setAttribute('aria-current', 'page');
        pageButtons.push(button);
        previous = page;
      }
      this.find('history-pages').replaceChildren(...pageButtons);
      let dates;
      try { dates = new Intl.DateTimeFormat(document.documentElement.lang || 'en-CA', { year: 'numeric', month: 'long', day: 'numeric', timeZone: model.summary?.timezone || 'America/Toronto' }); }
      catch { dates = new Intl.DateTimeFormat('en-CA', { dateStyle: 'long' }); }
      const output = rows.slice(start, end).map(row => {
        const tr = element('tr');
        const cell = element('td');
        const activity = element('div', 'bb-activity');
        const symbol = element('span', 'bb-activity-icon');
        const kind = ['earned', 'redeemed', 'adjustment', 'returned', 'reversed'].includes(row.kind) ? row.kind : 'adjustment';
        symbol.classList.add('bb-activity-icon--' + kind);
        const icon = kind === 'redeemed' ? 'reward' : ['adjustment', 'returned', 'reversed'].includes(kind) ? 'history' : row.channel === 'pos' ? 'store' : 'online';
        symbol.append(this.querySelector(`[data-bb-icon="${icon}"]`).content.cloneNode(true));
        const detail = element('div');
        const reference = row.reference ? text('order_' + (['pos', 'online'].includes(row.channel) ? row.channel : 'other'), { reference: row.reference }) : text('account_adjustment');
        const date = row.dateUnavailable ? text('date_unavailable') : row.cumulativeRefund ? text('refund_totals_through', { date: dates.format(new Date(row.at)) }) : dates.format(new Date(row.at));
        detail.append(element('strong', '', text(kind)), element('small', '', reference + (row.adjusted ? ' · ' + text('after_refunds') : '')), element('small', 'bb-mobile-date', date));
        activity.append(symbol, detail);
        cell.append(activity);
        tr.append(cell, element('td', 'bb-desktop-date', date), element('td', row.points < 0 ? 'bb-debit' : 'bb-credit', (row.points < 0 ? '−' : '+') + count(Math.abs(row.points))));
        return tr;
      });
      this.find('history').replaceChildren(...output);
    }
  }

  window.BeadBucks = { prepareCheckout, project, refresh, remove, isSelected: () => Boolean(model.selection || model.attempt || model.cancelling), isBusy: () => Boolean(model.checkout) };
  customElements.define('bead-bucks-panel', BeadBucksPanel);
  document.addEventListener('visibilitychange', scheduleAvailabilityCheck);
  document.addEventListener('cart-update', event => {
    try { acceptCart(event.detail); } catch { return; }
    repaint();
    if (!model.cart.items.length && !model.checkout && (model.selection || activeReward())) remove().catch(showError);
  });
  window.addEventListener('pageshow', event => { if (event.persisted && config.customerId && panels.size) refresh(true).catch(() => {}); });
  window.addEventListener('focus', () => {
    if (config.customerId && panels.size && !model.checkout && !requests.cancel && Date.now() - model.updated > 60000) {
      refresh(true).catch(() => {});
      if (Array.from(panels).some(panel => panel.dataset.view === 'account')) loadHistory(true);
    }
  });
})();
